import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import TabLayout from "src/components/base/tab-layout";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import SINGLE_RULESET_CONTEXT from "src/context/internal/ruleset";
import { BackButton, Button } from "src/components/base/button";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { ArrowTopRightOnSquareIcon, ChevronDownIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/20/solid";
import React, { Suspense } from "react";
import DeleteRulesetDialog from "src/components/dialogs/delete-ruleset";
import UpdateRulesetDialog from "src/components/dialogs/update-ruleset";
import Outlet from "src/components/base/outlet";

export const Route = createFileRoute("/_admin/a/rulesets/$rulesetId/_ruleset")({
    component: RouteComponent,
    loader: async ({ params }) => await ApiInternal.rulesets.get(params.rulesetId),
});

/** Base layout for a single ruleset's views */
function RouteComponent() {
    const [t] = useTranslation("admin-ruleset");

    const navigate = useNavigate();
    const router = useRouter();
    const ruleset = Route.useLoaderData();

    const [openUpdateRuleset, setOpenUpdateRuleset] = React.useState(false);
    const [openDeleteRuleset, setOpenDeleteRuleset] = React.useState(false);

    return (
        <SINGLE_RULESET_CONTEXT.Provider
            value={{ ruleset, reset: async () => await router.invalidate({ sync: true }) }}
        >
            <div className={"flex flex-col gap-6"}>
                <BackButton className={"self-start"} href={"/a/rulesets"}>
                    {t("button.back-button")}
                </BackButton>
                <TabLayout
                    heading={t("heading.ruleset", { ruleset: ruleset.name })}
                    headingDescription={ruleset.description}
                    headingChildren={
                        !ruleset.plugin_uuid ? (
                            <Dropdown>
                                <DropdownButton outline={true}>
                                    {t("button.edit")}
                                    <ChevronDownIcon />
                                </DropdownButton>
                                <DropdownMenu>
                                    <DropdownSection>
                                        <DropdownItem onClick={() => setOpenUpdateRuleset(true)}>
                                            <PencilSquareIcon />
                                            {t("button.edit-ruleset")}
                                        </DropdownItem>
                                    </DropdownSection>
                                    <DropdownSection>
                                        <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                        <DropdownItem onClick={() => setOpenDeleteRuleset(true)}>
                                            <TrashIcon />
                                            <DropdownLabel>{t("button.delete-ruleset")}</DropdownLabel>
                                        </DropdownItem>
                                    </DropdownSection>
                                </DropdownMenu>
                            </Dropdown>
                        ) : (
                            <Button plain href={"/a/plugins/$pluginId"} params={{ pluginId: ruleset.plugin_uuid }}>
                                <ArrowTopRightOnSquareIcon />
                            </Button>
                        )
                    }
                    tabs={
                        <TabMenu>
                            <Tab href={"/a/rulesets/$rulesetId/rules"}>{t("label.rules")}</Tab>
                            <Tab href={"/a/rulesets/$rulesetId/instances"}>{t("label.instances")}</Tab>
                        </TabMenu>
                    }
                >
                    <Outlet />
                </TabLayout>

                <Suspense>
                    <UpdateRulesetDialog
                        ruleset={ruleset}
                        isOpen={openUpdateRuleset}
                        onSubmit={async () => {
                            setOpenUpdateRuleset(false);
                            await router.invalidate({ sync: true });
                        }}
                        onClose={() => setOpenUpdateRuleset(false)}
                    />

                    <DeleteRulesetDialog
                        isOpen={openDeleteRuleset}
                        onClose={() => setOpenDeleteRuleset(false)}
                        onDelete={async () => {
                            setOpenDeleteRuleset(false);
                            await navigate({ to: "/a/rulesets", search: { search: "", page: 1, source: "" } });
                        }}
                        ruleset={ruleset}
                    />
                </Suspense>
            </div>
        </SINGLE_RULESET_CONTEXT.Provider>
    );
}
